import React, { createContext, useContext, useMemo } from "react";

// "Am I trading inside a league right now?"
//
// The Trade screen (char.jsx) and its parts — the right-hand watchlist, the order
// ticket — are shared between the main account and a league. Prop-drilling a
// league through a 4,000-line screen would be unmaintainable, so the league is
// published here and read by the few leaves that actually care:
//
//   • the order ticket tags every order with `game_id`, so it belongs to the
//     league's book and not the main account's
//   • the right-hand list shows the league's own assets instead of the user's
//     personal watchlists
//
// Outside a league the value is null and every consumer behaves exactly as it
// does today — that's what keeps the main Trade screen untouched.
const LeagueTradeContext = createContext(null);

export const useLeagueTrade = () => useContext(LeagueTradeContext);

// An "All markets" league opens on Bitcoin — it trades around the clock, so the
// chart has data whatever time a member first walks in.
export const ALL_MARKETS_DEFAULT_SYMBOL = "X:BTCUSD";

// ...but "no symbol list" does not always mean "all markets". A league created
// with an asset category and "All <category> selected" also stores no list, so it
// arrives here looking identical to a genuine all-markets league — and a STOCKS
// league was opening its chart on Bitcoin, which it cannot even trade. The
// category is the tie-breaker, so an open league opens on something its own
// market actually contains.
const CATEGORY_DEFAULT_SYMBOL = {
  stocks: "SPY",
  forex: "C:EURUSD",
  crypto: ALL_MARKETS_DEFAULT_SYMBOL,
};

// The symbol a league's chart should open on.
//
// Exported because TWO places need the same answer: this context (what the chart
// and the order ticket read) and LeagueTradePage, which seeds localStorage before
// the provider mounts. They each carried their own copy of the rule and the
// copies drifted, which is how the seed could write one symbol while the context
// reported another.
export const leagueDefaultSymbol = (game) => {
  const symbols = Array.isArray(game?.asset_symbols)
    ? game.asset_symbols.filter(Boolean)
    : [];
  if (symbols.length) return symbols[0];
  const key = String(game?.asset_category || "").toLowerCase();
  return CATEGORY_DEFAULT_SYMBOL[key] || ALL_MARKETS_DEFAULT_SYMBOL;
};

const CATEGORY_LABELS = {
  stocks: "Stocks",
  crypto: "Crypto",
  forex: "Forex",
  mixed: "Mixed",
};

// A ticker the chart can actually load: "AAPL", "X:BTCUSD", "C:EURUSD". Anything
// else — most often a league slug left in localStorage by an older screen — has
// to be ignored, or the chart opens blank on a symbol that doesn't exist.
//
// UPPERCASE is the whole test. The pattern used to accept any letters, so a
// lowercase league slug that happened to carry no hyphen ("dfgdfg", a real
// vanity_url here) sailed through as a symbol: /:slug/trade then asked the API
// for chart-stock-details?ticker=DFGDFG, got a 404, and rendered a blank chart
// with no way to recover. Every real symbol in this app is uppercase — the
// order ticket already normalises with String(ticker).toUpperCase() before it
// stores one — while slugs are lowercase by construction, so case is what tells
// the two apart. A lowercase value is now discarded and the caller falls back to
// something tradable (the league's first asset, or SPY on the main screen),
// which is strictly better than a chart that cannot load.
export const isPlausibleSymbol = (value) =>
  typeof value === "string" && /^([CX]:)?[A-Z.]{1,12}$/.test(value.trim());

export const LeagueTradeProvider = ({ game, children }) => {
  const value = useMemo(() => {
    if (!game?.id) return null;
    const symbols = Array.isArray(game.asset_symbols)
      ? game.asset_symbols.filter(Boolean)
      : [];
    const allMarkets = symbols.length === 0;
    const key = String(game.asset_category || "").toLowerCase();
    return {
      gameId: game.id,
      slug: game.vanity_url,
      title: game.title,
      // The league itself, for the trading rules.
      //
      // Client, "League Watchlist: launch behavior": an order ticket inside a
      // league has to be able to answer "can I trade this, and is the league even
      // open yet?" before it submits. Both answers come from the league's own
      // record (start_date, asset_category, asset_symbols) via
      // utils/leagueAssets, so the record is published rather than a snapshot of
      // it — `hasStarted` in particular is a function of the clock, and a value
      // memoised here would be wrong the moment the league opened.
      game,
      // The tickers picked at creation. Empty means the league was created with
      // "All" — every market is in play, so the panel offers stocks, forex and
      // crypto rather than a fixed list.
      symbols,
      allMarkets,
      // The market the league trades, so a fixed list is headed "Stocks" rather
      // than by the league's own name.
      category: key || "",
      categoryLabel: CATEGORY_LABELS[key] || "",
      // Where the chart opens: the league's first allowed asset; failing that, the
      // default for the market it trades; failing that, Bitcoin.
      defaultSymbol: leagueDefaultSymbol(game),
    };
  }, [game]);

  return (
    <LeagueTradeContext.Provider value={value}>{children}</LeagueTradeContext.Provider>
  );
};

export default LeagueTradeContext;
